import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExerciseService } from '../services/ExerciseService';
import type { PaginationInfo } from '../types';
import type { ExerciseItem, ExerciseType } from '../types/exercise';

export interface ExerciseFilters {
  searchTerm?: string;
  groups?: string[];
  exerciseTypes?: ExerciseType[];
  resistanceTypeIds?: string[];
  enabled?: boolean;
}

interface ExerciseFilterOption {
  value: string;
  label: string;
}

interface UseExercisesResult {
  exercises: ExerciseItem[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  totalExercises: number;
  groupOptions: ExerciseFilterOption[];
  resistanceTypeOptions: ExerciseFilterOption[];
  refreshExercises: () => Promise<void>;
}

const useExercises = (page: number, pageSize: number, filters: ExerciseFilters): UseExercisesResult => {
  const isEnabled = filters.enabled ?? true;
  const [exercises, setExercises] = useState<ExerciseItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [groupOptions, setGroupOptions] = useState<ExerciseFilterOption[]>([]);
  const [resistanceTypeOptions, setResistanceTypeOptions] = useState<ExerciseFilterOption[]>([]);
  const [loading, setLoading] = useState(isEnabled);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  const refreshExercises = useCallback(async () => {
    setReloadKey((currentKey) => currentKey + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadFilterOptions = async () => {
      try {
        const lookups = await ExerciseService.getExerciseLookups();

        if (!isMounted) {
          return;
        }

        setGroupOptions(lookups.muscleGroups.map((group) => ({ value: String(group.id), label: group.name })));
        setResistanceTypeOptions(lookups.resistanceTypes.map((type) => ({ value: String(type.id), label: type.name })));
      } catch (serviceError) {
        if (!isMounted) {
          return;
        }

        setGroupOptions([]);
        setResistanceTypeOptions([]);
        setError(serviceError instanceof Error ? serviceError.message : 'Não foi possível carregar os exercícios.');
      }
    };

    loadFilterOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const normalizedSearch = filters.searchTerm?.trim() ?? '';
  const normalizedGroups = useMemo(() => filters.groups ?? [], [filters.groups]);
  const normalizedExerciseTypes = useMemo(() => filters.exerciseTypes ?? [], [filters.exerciseTypes]);
  const normalizedResistanceTypeIds = useMemo(() => filters.resistanceTypeIds ?? [], [filters.resistanceTypeIds]);

  useEffect(() => {
    if (!isEnabled) {
      requestIdRef.current += 1;
      setLoading(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    const debounceHandle = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await ExerciseService.listPaginated(page, pageSize, {
          searchTerm: normalizedSearch,
          groupIds: normalizedGroups,
          exerciseTypes: normalizedExerciseTypes,
          resistanceTypeIds: normalizedResistanceTypeIds,
        });

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setExercises(response.data);
        setPagination(response.pagination);
        setError(null);
      } catch (serviceError) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setExercises([]);
        setPagination((currentPagination) => ({
          ...currentPagination,
          page,
          pageSize,
          totalRecords: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        }));
        setError(serviceError instanceof Error ? serviceError.message : 'Não foi possível carregar os exercícios.');
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(debounceHandle);
  }, [isEnabled, normalizedExerciseTypes, normalizedGroups, normalizedResistanceTypeIds, normalizedSearch, page, pageSize, reloadKey]);

  return {
    exercises,
    pagination,
    loading,
    error,
    totalExercises: pagination.totalRecords,
    groupOptions,
    resistanceTypeOptions,
    refreshExercises,
  };
};

export default useExercises;